import { Link, Navigate, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowRight, Tag } from "lucide-react";
import {
  getArticle,
  getRelatedArticles,
  BLOG_CATEGORIES,
} from "@/lib/blogData";
import { CITIES_DATA } from "@/lib/data";
import { renderMarkdown } from "@/lib/markdownRenderer";
import ResponsiveImage from "@/components/ResponsiveImage";
import SEO from "@/components/SEO";
import { SITE_URL } from "@/components/SEO";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const BlogArticle = () => {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) return <Navigate to="/blog" replace />;

  const cat = BLOG_CATEGORIES[article.category];
  const related = getRelatedArticles(slug, 3);

  // BlogPosting JSON-LD (great for SEO long-tail)
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.excerpt,
    image: `${SITE_URL}${article.image}`,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      "@type": "Organization",
      name: "Terre et Mer Conciergerie",
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "Terre et Mer Conciergerie",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/favicon.svg`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${article.slug}`,
    },
    keywords: article.keywords,
    articleSection: cat?.label || "Conseils",
    inLanguage: "fr-FR",
  };

  return (
    <div data-testid={`blog-article-${slug}`}>
      <SEO
        title={`${article.title}, Blog Terre et Mer`}
        description={article.excerpt}
        keywords={article.keywords}
        image={article.image}
        type="article"
        jsonLd={articleJsonLd}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: article.title.slice(0, 60), path: `/blog/${article.slug}` },
        ]}
      />

      {/* Header */}
      <header className="bg-sand pt-12 pb-12 lg:pt-16 lg:pb-16 border-b border-gold/15">
        <div className="container-tm max-w-3xl">
          <Link
            to="/blog"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-ink/60 hover:text-gold transition-colors mb-8"
          >
            <ArrowLeft size={13} strokeWidth={2} />
            Retour au blog
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-gold bg-white/80 px-3 py-1.5 rounded-full">
              <Tag size={10} strokeWidth={2} />
              {cat?.label || article.category}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-sans text-ink/50">
              <Calendar size={11} strokeWidth={1.8} />
              {formatDate(article.date)}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-sans text-ink/50">
              <Clock size={11} strokeWidth={1.8} />
              {article.readTime} min de lecture
            </span>
          </div>

          <h1
            data-testid="article-title"
            className="font-serif font-light text-ink text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] mb-6"
          >
            {article.title}
          </h1>

          <p className="font-sans text-lg md:text-xl text-ink/70 leading-relaxed">
            {article.excerpt}
          </p>
        </div>
      </header>

      {/* Hero image */}
      <div className="bg-sand pb-12 lg:pb-16">
        <div className="container-tm max-w-4xl">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden shadow-lift">
            <ResponsiveImage
              src={article.image}
              alt={article.imageAlt || article.title}
              eager
              sizes="(max-width: 1024px) 100vw, 1024px"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <article
        className="bg-white py-16 lg:py-20"
        data-testid="article-body"
      >
        <div className="container-tm max-w-3xl">
          {renderMarkdown(article.content)}
        </div>
      </article>

      {/* Cities related (if any) */}
      {article.relatedCities && article.relatedCities.length > 0 && (
        <section
          className="bg-cream py-16 lg:py-20 border-y border-gold/15"
          data-testid="article-related-cities"
        >
          <div className="container-tm max-w-5xl">
            <p className="overline mb-4">Villes mentionnées</p>
            <h2 className="font-serif font-light text-ink text-3xl md:text-4xl mb-10 leading-tight">
              Découvrez nos offres dédiées
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {article.relatedCities.map((slug) => {
                const city = CITIES_DATA[slug];
                if (!city) return null;
                return (
                  <Link
                    key={slug}
                    to={`/villes/${slug}`}
                    data-testid={`article-city-link-${slug}`}
                    className="group flex items-center gap-4 bg-white border border-ink/5 rounded-2xl p-5 hover:shadow-lift transition-all"
                  >
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={city.heroImage}
                        alt={city.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-1">
                        Département {city.department}
                      </p>
                      <h3 className="font-serif text-lg text-ink leading-tight">
                        {city.name}
                      </h3>
                    </div>
                    <ArrowRight
                      size={16}
                      strokeWidth={2}
                      className="text-sage group-hover:translate-x-1 transition-transform shrink-0"
                    />
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Related articles */}
      {related.length > 0 && (
        <section
          className="bg-white py-20 lg:py-24"
          data-testid="article-related-articles"
        >
          <div className="container-tm">
            <p className="overline mb-4">Articles dans la même catégorie</p>
            <h2 className="font-serif font-light text-ink text-3xl md:text-4xl mb-10 leading-tight">
              Pour <span className="italic text-gold">aller plus loin</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  data-testid={`related-article-${a.slug}`}
                  className="group bg-cream/30 hover:bg-white border border-ink/5 rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-lift"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-cream">
                    <ResponsiveImage
                      src={a.image}
                      alt={a.imageAlt || a.title}
                      sizes="(max-width: 640px) 100vw, 33vw"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-gold">
                      {BLOG_CATEGORIES[a.category]?.label}
                    </span>
                    <h3 className="font-serif text-lg text-ink leading-tight mt-3 mb-3">
                      {a.title}
                    </h3>
                    <span className="inline-flex items-center gap-2 text-sage font-sans text-[11px] uppercase tracking-[0.22em] font-semibold group-hover:gap-3 transition-all">
                      Lire l'article
                      <ArrowRight size={12} strokeWidth={2} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-ink text-white py-20 lg:py-24" data-testid="article-final-cta">
        <div className="container-tm text-center max-w-2xl">
          <p className="overline text-gold mb-6">Étude gratuite · sans engagement</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-5">
            Discutons de votre <span className="italic text-gold">projet</span>
          </h2>
          <p className="font-sans text-white/70 leading-relaxed mb-8">
            Notre équipe locale connaît votre territoire. En 15 minutes, nous évaluons votre potentiel locatif et vous proposons une stratégie sur mesure.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contact"
              data-testid="article-cta-contact"
              className="btn-accent inline-flex items-center"
            >
              Demander une étude
              <ArrowRight size={14} strokeWidth={2} className="ml-2" />
            </Link>
            <Link
              to="/blog"
              data-testid="article-cta-back-blog"
              className="btn-secondary inline-flex items-center"
            >
              <ArrowLeft size={14} strokeWidth={2} className="mr-2" />
              Tous les articles
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogArticle;
